<script setup>
import dayjs from 'dayjs';
import { defineProps, computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useState } from 'vuex-composition-helpers';
import AnimatedNumber from 'animated-number-vue';

import avatarPlaceholder from '@/assets/avatar-placeholder.png';
import request from '@/services/request';
import { getCurrencySymbol } from '@/utils';
import Iframe from '@/widgets/progress-bar/Iframe.vue';

const props = defineProps({
  campaignSlug: {
    type: String,
    require: false
  },
  fundraiserSlug: {
    type: String,
    require: false
  }
});

const campaign = ref(null); // Important to set to null, we use this to check if campaign settings are loaded or found at all
const fundraiser = ref(null);
const currentTime = ref(dayjs()); // Track current time for live countdown updates
const animatedProgressPercentage = ref(0); // For animated progress bar
let countdownInterval = null;

const showWidget = computed(() => {
  if (!campaign.value?.id) {
    return false;
  }

  if (props.fundraiserSlug) {
    return !!fundraiser.value?.id;
  }

  return true;
});

const { orgId } = useState(['orgId']);
const { settings: defaultSettings } = useState('publicOrgSettings', ['settings']);
const effectiveCampaignSlug = computed(() => props.campaignSlug || defaultSettings.value?.defaultSlug);
const hasExplicitCampaign = computed(() => Boolean(props.campaignSlug));

const fetchCampaign = async () => {
  if (!effectiveCampaignSlug.value) {
    return;
  }

  const { data, error } = await request.get(`/public/campaign/${orgId.value}/${effectiveCampaignSlug.value}`);
  if (error) {
    console.error('Error fetching campaign', error);
    return;
  }

  campaign.value = data;

  // Animate progress bar after data loads
  setTimeout(() => {
    animatedProgressPercentage.value = progressPercentage.value;
  }, 100);
};

const fetchFundraiser = async () => {
  if (!props.fundraiserSlug || !campaign.value?.id) {
    return;
  }

  const queryString = hasExplicitCampaign.value ? `?campaignId=${encodeURIComponent(campaign.value.id)}` : '';
  const { data, error } = await request.get(`/public/fundraiser/${orgId.value}/${props.fundraiserSlug}${queryString}`);
  if (error) {
    console.error('Error fetching fundraiser', error);
    return;
  }

  fundraiser.value = data.fundraiser;

  // Animate progress bar after data loads
  setTimeout(() => {
    animatedProgressPercentage.value = progressPercentage.value;
  }, 100);
};

const pollCampaign = async () => {
  if (!effectiveCampaignSlug.value) {
    return;
  }

  const { data, error } = await request.get(`/public/campaign/${orgId.value}/${effectiveCampaignSlug.value}`);
  if (error || !data) return;

  if (data.chargeTotal !== campaign.value?.chargeTotal) {
    campaign.value = data;
    animatedProgressPercentage.value = progressPercentage.value;
  }
};

const pollFundraiser = async () => {
  if (!props.fundraiserSlug || !campaign.value?.id) {
    return;
  }

  const queryString = hasExplicitCampaign.value ? `?campaignId=${encodeURIComponent(campaign.value.id)}` : '';
  const { data, error } = await request.get(`/public/fundraiser/${orgId.value}/${props.fundraiserSlug}${queryString}`);
  if (error || !data?.fundraiser) return;

  const nextFundraiser = data.fundraiser;
  if (nextFundraiser.chargeTotal !== fundraiser.value?.chargeTotal || nextFundraiser.fundraiserInfo?.goal !== fundraiser.value?.fundraiserInfo?.goal) {
    fundraiser.value = nextFundraiser;
    animatedProgressPercentage.value = progressPercentage.value;
  }
};

const currencySymbol = computed(() => {
  const { orgDefaultCurrency } = useState('donation', ['orgDefaultCurrency']);
  const currency = campaign.value?.currency || orgDefaultCurrency.value;
  return currency ? getCurrencySymbol(currency) : '';
});

const goalMeter = computed(() => {
  const checkoutSettings = campaign.value?.checkoutSettings || {};
  const meter = checkoutSettings.goalMeter;

  if (!meter) {
    return null;
  }

  return {
    ...meter,
    endDate: meter.endDate || checkoutSettings.endDate
  };
});

const campaignEndDate = computed(() => {
  return goalMeter.value?.endDate || campaign.value?.checkoutSettings?.endDate;
});

const hideTargetAmount = computed(() => goalMeter.value?.hideTargetAmount === true);

const amountRaised = computed(() => {
  if (props.fundraiserSlug) {
    return Number(fundraiser.value?.chargeTotal || 0);
  }

  return Number(campaign.value?.chargeTotal || 0);
});

const unitBased = computed(() => goalMeter.value?.type === 'units');

const fulfilledUnits = computed(() => {
  const unitCost = Number(goalMeter.value?.unitCost || 0);
  if (!unitCost) {
    return 0;
  }

  return Math.floor(amountRaised.value / unitCost);
});

const amountTarget = computed(() => {
  if (props.fundraiserSlug) {
    return Number(fundraiser.value?.fundraiserInfo?.goal || 0);
  }

  return Number(goalMeter.value?.amountTarget || 0);
});

const unitTarget = computed(() => {
  if (props.fundraiserSlug) {
    return Number(fundraiser.value?.fundraiserInfo?.goal || 0);
  }

  return Number(goalMeter.value?.unitsTarget || 0);
});

const progressValue = computed(() => {
  if (unitBased.value) {
    return fulfilledUnits.value;
  }

  return amountRaised.value;
});

const progressTarget = computed(() => {
  if (unitBased.value) {
    return unitTarget.value;
  }

  return amountTarget.value;
});

const timeLeft = computed(() => {
  const endDate = campaignEndDate.value;
  if (!endDate) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
  }

  const now = currentTime.value; // Use reactive currentTime instead of dayjs()
  const end = dayjs(endDate);

  const totalHours = end.diff(now, 'hours');
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = end.diff(now, 'minutes') % 60;
  const seconds = end.diff(now, 'seconds') % 60;

  return {
    days: Math.max(days, 0),
    hours: Math.max(hours, 0),
    minutes: Math.max(minutes, 0),
    seconds: Math.max(seconds, 0)
  };
});

const progressPercentage = computed(() => {
  if (!progressTarget.value) {
    return 0;
  }

  const percentage = (100 / progressTarget.value) * progressValue.value;
  return Math.min(percentage, 100);
});

const displayProgressPercentage = computed(() => {
  return Math.ceil(progressPercentage.value);
});

const showFundraiserIdentity = computed(() => {
  return !!props.fundraiserSlug && !!fundraiser.value?.id;
});

const fundraiserUser = computed(() => {
  return fundraiser.value?.user || {};
});

const fundraiserNameLines = computed(() => {
  const nickname = fundraiser.value?.fundraiserInfo?.nickname?.trim() || fundraiser.value?.nickname?.trim();
  if (nickname) {
    return [nickname];
  }

  const firstName = fundraiserUser.value.firstName || '';
  const lastName = fundraiserUser.value.lastName || '';
  const lines = [firstName.trim(), lastName.trim()].filter(Boolean);
  return lines.length ? lines : ['Fundraiser'];
});

const fundraiserAvatarUrl = computed(() => {
  return fundraiserUser.value.avatarUrl || avatarPlaceholder;
});

const fundraiserAvatarAlt = computed(() => {
  return fundraiserNameLines.value.join(' ');
});

const baseContainerHeight = computed(() => {
  if (hideTargetAmount.value) {
    return campaignEndDate.value ? 190 : 98;
  }

  return campaignEndDate.value ? 278 : 186;
});

const containerHeight = computed(() => {
  const fundraiserIdentityHeight = showFundraiserIdentity.value ? 132 : 0;
  return `${baseContainerHeight.value + fundraiserIdentityHeight}px`;
});

// Start countdown timer and listen for new donations
onMounted(() => {
  // Update countdown every second so the seconds block ticks live.
  countdownInterval = setInterval(() => {
    currentTime.value = dayjs();
  }, 1000);

  // Re-fetch stats when the donation feed detects new donations
  window.addEventListener('new-donations', pollCampaign);
  window.addEventListener('new-donations', pollFundraiser);
});

// Cleanup on unmount
onBeforeUnmount(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
  window.removeEventListener('new-donations', pollCampaign);
  window.removeEventListener('new-donations', pollFundraiser);
});

fetchCampaign().then(fetchFundraiser);
</script>

<template>
  <div class="double--progress-bar-widget-frame" v-if="showWidget">
    <Iframe :height="containerHeight">
      <div class="progress-bar-widget p-8 bg-white border border-gray-light rounded-lg flex flex-col gap-8 items-center py-8 px-6 w-full">
        <!-- Fundraiser Identity -->
        <div v-if="showFundraiserIdentity" class="progress-bar-widget__fundraiser-identity">
          <div class="progress-bar-widget__fundraiser-avatar">
            <img :src="fundraiserAvatarUrl" :alt="fundraiserAvatarAlt" />
          </div>
          <div class="progress-bar-widget__fundraiser-name">
            <p v-for="nameLine in fundraiserNameLines" :key="nameLine">{{ nameLine }}</p>
          </div>
        </div>

        <!-- Raised Stats -->
        <div class="flex flex-col gap-2 items-center w-full text-center">
          <p class="font-nunito font-extrabold text-26 leading-1.23 text-black-00 m-0 whitespace-nowrap">
            <template v-if="unitBased">
              <animated-number :value="fulfilledUnits" :duration="1400" :round="true" :formatValue="(value) => value.toLocaleString()" />
              {{ goalMeter.unitName }}
            </template>
            <template v-else>
              {{ currencySymbol
              }}<animated-number :value="amountRaised" :duration="1400" :round="true" :formatValue="(value) => value.toLocaleString()" /> Raised
            </template>
          </p>
          <div v-if="!hideTargetAmount" class="progress-bar-widget__goal-group">
            <p class="font-nunito font-normal text-lg leading-1.364 text-donation-gray m-0">
              <template v-if="unitBased">Out of {{ unitTarget.toLocaleString() }} {{ goalMeter.unitName }}</template>
              <template v-else>Out of {{ currencySymbol }}{{ amountTarget.toLocaleString() }}</template>
            </p>
            <span class="progress-bar-widget__goal-divider" aria-hidden="true"></span>
            <p class="font-nunito font-normal text-lg leading-1.364 text-donation-gray m-0">{{ displayProgressPercentage }}%</p>
          </div>
        </div>

        <!-- Progress Bar -->
        <div v-if="!hideTargetAmount" class="progress-bar-widget__progress-bar bg-gray-light w-full h-6 rounded-full relative overflow-hidden">
          <div
            class="h-full rounded-full progress-bar-widget__progress-fill"
            :style="{
              width: animatedProgressPercentage + '%',
              background: campaign.checkoutSettings.themeColor || '#97DA55',
              transition: 'width 1.4s ease'
            }"
          ></div>
        </div>

        <!-- Countdown -->
        <div v-if="campaignEndDate" class="progress-bar-widget__countdown flex flex-col w-full">
          <div class="progress-bar-widget__countdown-blocks flex gap-2 items-center justify-center w-full">
            <div class="bg-gray-50 flex flex-col items-center justify-center px-3 py-2 rounded-lg countdown-block">
              <p class="font-nunito font-bold text-lg leading-1.364 text-black-00 m-0">{{ String(timeLeft.days).padStart(2, '0') }}</p>
              <p class="font-nunito-sans font-semibold text-sm leading-1.286 text-donation-gray m-0">Days</p>
            </div>
            <div class="bg-gray-50 flex flex-col items-center justify-center px-3 py-2 rounded-lg countdown-block">
              <p class="font-nunito font-bold text-lg leading-1.364 text-black-00 m-0">{{ String(timeLeft.hours).padStart(2, '0') }}</p>
              <p class="font-nunito-sans font-semibold text-sm leading-1.286 text-donation-gray m-0">
                <span class="countdown-label--full">Hours</span>
                <span class="countdown-label--compact">Hrs</span>
              </p>
            </div>
            <div class="bg-gray-50 flex flex-col items-center justify-center px-3 py-2 rounded-lg countdown-block">
              <p class="font-nunito font-bold text-lg leading-1.364 text-black-00 m-0">{{ String(timeLeft.minutes).padStart(2, '0') }}</p>
              <p class="font-nunito-sans font-semibold text-sm leading-1.286 text-donation-gray m-0">
                <span class="countdown-label--full">Minutes</span>
                <span class="countdown-label--compact">Min</span>
              </p>
            </div>
            <div class="bg-gray-50 flex flex-col items-center justify-center px-3 py-2 rounded-lg countdown-block">
              <p class="font-nunito font-bold text-lg leading-1.364 text-black-00 m-0">{{ String(timeLeft.seconds).padStart(2, '0') }}</p>
              <p class="font-nunito-sans font-semibold text-sm leading-1.286 text-donation-gray m-0">
                <span class="countdown-label--full">Seconds</span>
                <span class="countdown-label--compact">Sec</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </Iframe>
  </div>
</template>
